import { CheckOutlined, FileTextOutlined } from '@ant-design/icons';
import {
  counted,
  describeError,
  dirhams,
  FINANCE_ERRORS,
  fullDate,
  present,
} from '@horof/shared';
import { Alert, App, Button, Result, Segmented, Table, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useClient, useQuery } from 'urql';
import { financeContext, useCanWrite } from './data';
import { InKindPanel, TransferPanel } from './MovementsScreens';
import { OperationPanel } from './OperationPanel';
import { useSplit } from './panel';
import {
  KIND_LABELS,
  type ReviewItem,
  type ReviewKind,
  ReviewQueueDocument,
  SetReviewDocument,
} from './review-data';
import { type FitColumn, useFitColumns } from './table-fit';
import { ListHeader } from './ui';

const KIND_COLORS: Record<ReviewKind, string> = {
  operation: 'default',
  transfer: 'blue',
  in_kind: 'purple',
};

const REVIEW_ERRORS = {
  ...FINANCE_ERRORS,
  REVIEW_TARGET_NOT_FOUND: 'بعض العناصر لم تعد موجودة. حدّث الصفحة.',
};

/**
 * «المراجعة»: everything waiting for a review, operations, transfers and
 * in-kind contributions together, the oldest request first (arqam doc 54).
 * A row opens its full form beside the list; the treasurer clears one row
 * or the ticked ones. The committee reads, and asks from a row's panel.
 */
export function ReviewScreen() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const client = useClient();
  const { modal, message } = App.useApp();
  const split = useSplit();
  const { ref: fitRef, fit } = useFitColumns();
  const [params, setParams] = useSearchParams();
  const filter = params.get('k') as ReviewKind | null;
  const panelKind = params.get('kind') as ReviewKind | null;
  const [selected, setSelected] = useState<string[]>([]);
  const [clearing, setClearing] = useState<string | null>(null);
  const [{ data, error, fetching }] = useQuery({
    query: ReviewQueueDocument,
    variables: { kind: filter },
    context: useMemo(() => financeContext, []),
  });
  const rows = present(data?.financeReviewQueue?.nodes);
  const total = data?.financeReviewQueue?.totalCount ?? 0;

  const open = (id: string | null, kind: ReviewKind | null) =>
    split.open(id, { kind });
  const close = () => open(null, null);

  /** Clears the review of `items`: one call per kind, all or nothing each. */
  async function clear(items: ReviewItem[]) {
    let done = 0;
    for (const kind of Object.keys(KIND_LABELS) as ReviewKind[]) {
      const ids = items.filter((i) => i.kind === kind).map((i) => i.id);
      if (!ids.length) continue;
      const r = await client
        .mutation(
          SetReviewDocument,
          { kind, ids, needsReview: false },
          financeContext,
        )
        .toPromise();
      if (r.error) {
        message.error(
          describeError(r.error, 'تعذّرت إزالة علامة المراجعة.', REVIEW_ERRORS),
          6,
        );
        break;
      }
      done += r.data?.appSetReview?.result ?? ids.length;
    }
    if (done) message.success(`رُوجع ${reviewedCount(done)}`);
    setSelected((s) => s.filter((id) => !items.some((i) => i.id === id)));
  }

  async function clearSelected() {
    const items = rows.filter((r) => selected.includes(r.id));
    const ok = await modal.confirm({
      title: 'تمت المراجعة',
      content: `تُزال علامة المراجعة عن ${reviewedCount(items.length)}. تبقى الملاحظات في سجل التعديلات.`,
      okText: 'نعم، تمت المراجعة',
      cancelText: 'رجوع',
    });
    if (ok) await clear(items);
  }

  const columns: FitColumn<ReviewItem>[] = [
    {
      key: 'kind',
      drop: 4,
      title: 'النوع',
      width: 110,
      render: (_, r) => (
        <Tag color={KIND_COLORS[r.kind as ReviewKind]}>
          {KIND_LABELS[r.kind as ReviewKind] ?? r.kind}
        </Tag>
      ),
    },
    {
      key: 'date',
      title: 'التاريخ',
      width: 96,
      render: (_, r) => <span className="ltr-num">{r.date}</span>,
    },
    {
      key: 'what',
      title: 'البيان وما يجب التحقق منه',
      render: (_, r) => (
        <div className="desk-review-item">
          <div>{r.description || '—'}</div>
          {r.reviewNote && (
            <div className="desk-review-note">{r.reviewNote}</div>
          )}
        </div>
      ),
    },
    {
      key: 'where',
      drop: 2,
      title: 'الحساب · المشروع',
      width: 190,
      ellipsis: true,
      render: (_, r) => (
        <span className="field-hint">
          {[r.account, r.project].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      key: 'receipt',
      drop: 3,
      title: 'وصل',
      width: 64,
      render: (_, r) =>
        r.hasReceipt ? <FileTextOutlined className="receipt-mark" /> : null,
    },
    {
      key: 'amount',
      title: 'المبلغ',
      width: 124,
      render: (_, r) => <strong>{dirhams(r.amount)}</strong>,
    },
    {
      key: 'asked',
      drop: 1,
      title: 'طُلبت',
      width: 140,
      render: (_, r) =>
        r.reviewRequestedAt ? (
          <span className="field-hint">
            {fullDate(r.reviewRequestedAt)}
            {r.reviewRequestedBy && <div>{r.reviewRequestedBy}</div>}
          </span>
        ) : (
          <span className="field-hint">قديمة</span>
        ),
    },
  ];
  if (canWrite) {
    columns.push({
      key: 'done',
      width: 140,
      render: (_, r) => (
        <Button
          size="small"
          icon={<CheckOutlined />}
          loading={clearing === r.id}
          onClick={async (e) => {
            e.stopPropagation();
            setClearing(r.id);
            await clear([r]);
            setClearing(null);
          }}
        >
          تمت المراجعة
        </Button>
      ),
    });
  }

  // «حفظ والتالي» in an operation's panel: the next operation of the list.
  const nextOperation = (id: string) => {
    const i = rows.findIndex((r) => r.id === id);
    return rows.slice(i + 1).find((r) => r.kind === 'operation')?.id ?? null;
  };

  return (
    <div className="desk-split">
      <section className="desk-page" ref={fitRef}>
        <ListHeader
          title="المراجعة"
          count={data ? total : undefined}
          description={
            canWrite
              ? 'افتح العنصر لتصحيحه، ثم «تمت المراجعة». الأقدم أولاً.'
              : 'أمين المال يراجع هذه العناصر. لطلب مراجعة، افتح أي عملية واضغط «طلب مراجعة».'
          }
        />
        <div className="ui-table-card">
          <div className="desk-toolbar">
            <Segmented<string>
              value={filter ?? 'all'}
              onChange={(v) =>
                setParams(
                  (p) => {
                    const next = new URLSearchParams(p);
                    if (v === 'all') next.delete('k');
                    else next.set('k', v);
                    return next;
                  },
                  { replace: true },
                )
              }
              options={[
                { value: 'all', label: 'الكل' },
                { value: 'operation', label: 'العمليات' },
                { value: 'transfer', label: 'التحويلات' },
                { value: 'in_kind', label: 'المساهمات العينية' },
              ]}
            />
          </div>

          {canWrite && selected.length > 0 && (
            <div className="desk-drafts is-selection" role="status">
              <span>
                <strong className="ltr-num">{selected.length}</strong> مختارة
              </span>
              <Button type="primary" onClick={clearSelected}>
                تمت مراجعة المحدَّد
              </Button>
              <Button onClick={() => setSelected([])}>إلغاء الاختيار</Button>
            </div>
          )}

          {error && (
            <Alert
              type="error"
              showIcon
              message={describeError(error, 'تعذّر تحميل قائمة المراجعة.')}
            />
          )}

          {data && total === 0 ? (
            <Result
              status="success"
              title="لا شيء ينتظر المراجعة"
              subTitle={filter ? 'في هذا النوع.' : undefined}
            />
          ) : (
            <Table<ReviewItem>
              rowKey="id"
              size="small"
              tableLayout="fixed"
              columns={fit(columns, { reserve: canWrite ? 56 : 24 })}
              dataSource={rows}
              loading={fetching && !data}
              pagination={false}
              scroll={{ y: 'calc(100vh - 250px)' }}
              rowSelection={
                canWrite
                  ? {
                      columnWidth: 32,
                      selectedRowKeys: selected,
                      onChange: (keys) => setSelected(keys.map(String)),
                    }
                  : undefined
              }
              rowClassName={(r) =>
                r.id === split.panel ? 'is-open is-clickable' : 'is-clickable'
              }
              onRow={(r) => ({
                onClick: (e) => {
                  if (
                    (e.target as HTMLElement).closest(
                      '.ant-table-selection-column',
                    )
                  ) {
                    return;
                  }
                  open(r.id, r.kind as ReviewKind);
                },
              })}
            />
          )}
        </div>
      </section>

      {split.panel && panelKind === 'operation' && (
        <OperationPanel
          key={split.panel}
          id={split.panel}
          copyFrom={null}
          onSaved={() => {}}
          onDirtyChange={split.onDirtyChange}
          onClose={close}
          onOpen={(id) => open(id, 'operation')}
          onNew={(from) =>
            navigate(`/operations?panel=new${from ? `&copy=${from}` : ''}`)
          }
          nextId={nextOperation(split.panel)}
        />
      )}
      {split.panel && panelKind === 'transfer' && (
        <TransferPanel
          key={split.panel}
          id={split.panel}
          onClose={close}
          onOpen={(id) => open(id, 'transfer')}
          onDirtyChange={split.onDirtyChange}
        />
      )}
      {split.panel && panelKind === 'in_kind' && (
        <InKindPanel
          key={split.panel}
          id={split.panel}
          onClose={close}
          onOpen={(id) => open(id, 'in_kind')}
          onDirtyChange={split.onDirtyChange}
        />
      )}
    </div>
  );
}

/** «عنصر واحد», «3 عناصر», «11 عنصراً». */
const reviewedCount = (n: number) =>
  counted(n, {
    one: 'عنصر واحد',
    two: 'عنصران',
    few: 'عناصر',
    many: 'عنصراً',
    hundred: 'عنصر',
  });
